import { UserPayload } from "../models/SiteModel";

const URLAPI = process.env.REACT_APP_BASE_URL;

const createHttpError = async (response: Response) => {
  const text = await response.text();
  return new Error(text || `HTTP ${response.status}`);
};

export type CompraProducto = {
  idProducto: number;
  idPresentacionVenta?: number;
  esPresentacionVenta?: boolean;
  codigo: string;
  producto: string;
  descripcion: string;
  presentacion: string;
  stock: number;
  precioCompra: number;
  precioCompraBruto: number;
  ivaCompra: number;
  ivaVenta: number;
  rubro: string;
  subrubro: string;
  unidadesPorPresentacion?: number;
};

export type CompraProductoPage = {
  items: CompraProducto[];
  total: number;
  page: number;
  pageSize: number;
};

export type CompraReporteItem = {
  idCompra: number;
  idLocal: number;
  fecha: string;
  fechaIva: string;
  idProveedor: number;
  proveedor: string;
  tipo: string;
  letraFactura: string;
  puntoVenta: string;
  numeroFactura: string;
  subtotal: number;
  iva105: number;
  iva21: number;
  iva27: number;
  noGravado: number;
  percepcionIva: number;
  ingresosBrutos: number;
  impuestosInternos: number;
  tem: number;
  bonificacion: number;
  totalFactura: number;
  formaPago: string;
  numeroRemito: string;
};

export type CompraDetalleItem = {
  producto: string;
  cantidad: number;
  forma: string;
  cantidadForma: number;
  unidadesPorForma: number;
  cantidadBase: number;
  costoPorForma: number;
  unitario: number;
  total: number;
};

export type CompraReportePage = {
  items: CompraReporteItem[];
  total: number;
  page: number;
  pageSize: number;
  totalSubtotal: number;
  totalIva: number;
  totalPercepciones: number;
  totalBonificacion: number;
  totalFactura: number;
};

export type CompraLinea = {
  idProducto: number;
  idPresentacionVenta?: number;
  presentacionVenta?: string;
  cantidadPresentacion?: number;
  unidadesPorPresentacion?: number;
  cantidadBase?: number;
  producto: string;
  cantidad: number;
  ivaCompra: number;
  unitarioSinIva: number;
  unitarioConIva: number;
  totalConIva: number;
  precioPresentacionSinIva?: number;
  precioPresentacionConIva?: number;
};

export type CompraGuardarRequest = {
  tipoComprobante: number;
  idLocal: number;
  puntoVenta: number;
  idProveedor: number;
  proveedor: string;
  numeroRemito: string;
  numeroFacturaPv: string;
  numeroFactura: string;
  letraFactura: string;
  fecha: string;
  fechaIva: string;
  noGravado: number;
  subtotal: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  ingresosBrutos: number;
  impuestosInternos: number;
  tem: number;
  percepcionIva: number;
  bonificacion: number;
  totalFactura: number;
  formaPago?: number;
  vencimiento: string;
  detalle: CompraLinea[];
  vencimientos: { fecha: string; importe: number }[];
};

export type FormaProducto = {
  idPresentacion: number;
  idProducto: number;
  nombre: string;
  unidades: number;
  activa: boolean;
  habilitadaCompra: boolean;
  habilitadaVenta: boolean;
};

export async function fetchFormasProducto(user: UserPayload, idProducto: number, idLocal: number): Promise<FormaProducto[]> {
  const response = await fetch(`${URLAPI}/Compra/Productos/${idProducto}/Formas?idLocal=${idLocal}`, {
    headers: { Authorization: "Bearer " + user.token },
  });
  if (!response.ok) throw await createHttpError(response);
  const data = await response.json();
  const rows = data?.$values ?? data ?? [];
  return rows.map((row: any) => ({
    idPresentacion: row.IDPRESENTACION ?? row.idPresentacion ?? 0,
    idProducto: row.IDPRODUCTO ?? row.idProducto ?? idProducto,
    nombre: row.NOMBRE ?? row.nombre ?? "",
    unidades: Number(row.UNIDADES ?? row.unidades ?? 1),
    activa: Boolean(row.ACTIVA ?? row.activa ?? true),
    habilitadaCompra: Boolean(row.HABILITADACOMPRA ?? row.habilitadaCompra ?? true),
    habilitadaVenta: Boolean(row.HABILITADAVENTA ?? row.habilitadaVenta ?? true),
  }));
}

export async function fetchGuardarFormaProducto(user: UserPayload, idProducto: number, forma: { idPresentacion?: number; nombre: string; unidades: number; habilitadaCompra: boolean; habilitadaVenta: boolean }) {
  const response = await fetch(`${URLAPI}/Compra/Productos/${idProducto}/Formas`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + user.token },
    body: JSON.stringify({ idPresentacion: forma.idPresentacion ?? 0, idProducto, nombre: forma.nombre, unidades: String(forma.unidades), activa: true, habilitadaCompra: forma.habilitadaCompra, habilitadaVenta: forma.habilitadaVenta, preciosPorLocal: [] }),
  });
  if (!response.ok) throw await createHttpError(response);
  return response.json();
}

export async function fetchGetProductosCompra(
  idLocal: string | undefined,
  user: UserPayload,
  page: number,
  pageSize: number,
  filters: { search?: string; idProveedor?: number },
  signal?: AbortSignal
): Promise<CompraProductoPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.idProveedor) params.set("idProveedor", String(filters.idProveedor));

  const response = await fetch(`${URLAPI}/Compra/Productos/${idLocal}?${params.toString()}`, {
    method: "GET",
    signal,
    headers: { Authorization: "Bearer " + user.token },
  });

  if (!response.ok) {
    throw await createHttpError(response);
  }

  const data = await response.json();
  const items = data.items ?? data.Items ?? [];
  return {
    items: items.map((item: any) => ({ ...item, idLocal: Number(item.idLocal ?? item.IdLocal ?? 0) })),
    total: data.total ?? data.Total ?? 0,
    page: data.page ?? data.Page ?? page,
    pageSize: data.pageSize ?? data.PageSize ?? pageSize,
  };
}

export async function fetchGetReporteCompras(
  user: UserPayload,
  page: number,
  pageSize: number,
  filters: { fechaDesde: string; fechaHasta: string; idProveedor?: number },
  signal?: AbortSignal
): Promise<CompraReportePage> {
  const params = new URLSearchParams({
    fechaDesde: filters.fechaDesde,
    fechaHasta: filters.fechaHasta,
    page: String(page),
    pageSize: String(pageSize),
  });
  if (filters.idProveedor) params.set("idProveedor", String(filters.idProveedor));

  const response = await fetch(`${URLAPI}/Compra/Reporte?${params.toString()}`, {
    method: "GET",
    signal,
    headers: { Authorization: "Bearer " + user.token },
  });

  if (!response.ok) {
    throw await createHttpError(response);
  }

  const data = await response.json();
  const items = data.items ?? data.Items ?? [];
  return {
    items: items.map((item: any) => ({ ...item, idLocal: Number(item.idLocal ?? item.IdLocal ?? 0) })),
    total: data.total ?? data.Total ?? 0,
    page: data.page ?? data.Page ?? page,
    pageSize: data.pageSize ?? data.PageSize ?? pageSize,
    totalSubtotal: data.totalSubtotal ?? data.TotalSubtotal ?? 0,
    totalIva: data.totalIva ?? data.TotalIva ?? 0,
    totalPercepciones: data.totalPercepciones ?? data.TotalPercepciones ?? 0,
    totalBonificacion: data.totalBonificacion ?? data.TotalBonificacion ?? 0,
    totalFactura: data.totalFactura ?? data.TotalFactura ?? 0,
  };
}

export async function fetchGetDetalleCompra(
  user: UserPayload,
  idCompra: number,
  idLocal: number
): Promise<CompraDetalleItem[]> {
  const response = await fetch(`${URLAPI}/Compra/Detalle/${idCompra}/${idLocal}`, {
    method: "GET",
    headers: { Authorization: "Bearer " + user.token },
  });
  if (!response.ok) throw await createHttpError(response);

  const data = await response.json();
  const rows = Array.isArray(data) ? data : data?.$values ?? data?.rows ?? data?.Rows ?? [];
  return rows.map((row: any) => ({
    producto: row.producto ?? row.PRODUCTO ?? "",
    cantidad: Number(row.cantidad ?? row.CANTIDAD ?? 0),
    forma: row.forma ?? row.FORMA ?? "Sin forma registrada",
    cantidadForma: Number(row.cantidadForma ?? row.CANTIDADFORMA ?? 0),
    unidadesPorForma: Number(row.unidadesPorForma ?? row.UNIDADESPORFORMA ?? 0),
    cantidadBase: Number(row.cantidadBase ?? row.CANTIDADBASE ?? 0),
    costoPorForma: Number(row.costoPorForma ?? row.COSTOPORFORMA ?? 0),
    unitario: Number(row.unitario ?? row.UNITARIO ?? 0),
    total: Number(row.total ?? row.TOTAL ?? 0),
  }));
}

export async function fetchGuardarCompra(user: UserPayload, request: CompraGuardarRequest) {
  const response = await fetch(`${URLAPI}/Compra/Guardar`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + user.token,
    },
    body: JSON.stringify(request),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message ?? data?.Message ?? "No se pudo guardar la compra.");
  }

  return data;
}
